import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { AskScope, askPromptLimit, parseAskChatCommand, useAsk } from '@shared/scenes/ask';
import { ASK, useFeatureFlag } from '@shared/util/featureFlags';
import { useChannel, useChannelDisplayName } from '@shared/util/hooks';

/**
 * - `not-a-command`: an ordinary message. Send it.
 * - `taken`: Ask has the question — asked outright, or waiting in its composer. The input can
 *   let go of the text.
 * - `returned`: Ask was offered the question and handed it back (the user closed the panel
 *   without answering). The text is still the user's, so leave it where they typed it.
 */
export type AskChatCommandOutcome = 'not-a-command' | 'taken' | 'returned';

/**
 * The Ask context for a chat input: the workspace the message was addressed to.
 *
 * Undefined when there is nothing to scope a question to — Ask is off, or the flow has no
 * workspace yet, as in a new message before its channel exists. Callers treat that as "no Ask
 * here" rather than falling back to a wider scope: a question about "everything" is not what
 * someone typing into a workspace asked for.
 */
export function useAskChatScope(channelSlug: string | undefined): AskScope | undefined {
  const isAskEnabled = useFeatureFlag(ASK);
  const channel = useChannel({ channelSlug });
  const channelName = useChannelDisplayName(channel);

  return useMemo(() => {
    if (!isAskEnabled || isNil(channel) || isNil(channelSlug)) {
      return undefined;
    }
    return { type: 'channel', id: String(channel.id), label: channelName, channelSlug };
  }, [isAskEnabled, channel, channelSlug, channelName]);
}

interface UseAskChatCommandOptions {
  /** The workspace the message was going to. Ask is scoped to it. */
  channelSlug?: string;
  /**
   * Whether the input is editing a sent message. Editing one that happens to start with the
   * command is not a request to run it.
   */
  isEditing?: boolean;
}

/**
 * Runs `/ask` from the chat input: the question goes to Ask, scoped to the workspace the
 * message was addressed to, instead of being sent as a message.
 */
export function useAskChatCommand({ channelSlug, isEditing }: UseAskChatCommandOptions) {
  const { open } = useAsk();
  const scope = useAskChatScope(channelSlug);

  return useCallback(
    async (text: string): Promise<AskChatCommandOutcome> => {
      if (isNil(scope) || isEditing) {
        return 'not-a-command';
      }
      const command = parseAskChatCommand(text);
      if (isNil(command)) {
        return 'not-a-command';
      }

      const result = await open(scope, {
        prompt: command.prompt,
        // Submitting the message was the send, so there is nothing further to press — unless
        // the question is longer than Ask accepts, where sending it would be dropped on the
        // floor. Seeding it puts it in the composer, which says what is wrong and by how much.
        autoSubmit: command.prompt.length <= askPromptLimit(scope),
      });
      return result === 'dismissed' ? 'returned' : 'taken';
    },
    [scope, isEditing, open],
  );
}

/**
 * Opens Ask for the workspace from the chat input's own controls, carrying whatever the user
 * has typed so far into the composer.
 *
 * The draft is seeded rather than asked: picking this from a menu is not pressing send. It is
 * also left in the chat input, since the user never asked to stop writing their message — the
 * `/ask` command is where handing the text over is the whole intent.
 */
export function useAskWithChatDraft(channelSlug: string | undefined) {
  const { open } = useAsk();
  const scope = useAskChatScope(channelSlug);

  const askWithDraft = useCallback(
    (draft: string) => {
      if (isNil(scope)) {
        return;
      }
      const prompt = draft.trim();
      void open(scope, {
        prompt: prompt.length <= askPromptLimit(scope) ? prompt : undefined,
      });
    },
    [scope, open],
  );

  return { isAvailable: !isNil(scope), askWithDraft };
}
